import FundSection from './FundSection'
import { useMemo, useState } from 'react'
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import FundSelector from '../FundSelector'
import { rememberFundChoice } from '../fundScope'
import ObjectDrawer, { type DrawerTarget } from '../ObjectDrawer'
import { manualMovementHref } from '../manualMovement'
import FundOverviewTab from '../fund/FundOverviewTab'
import type { MatrixRow, WalkNode } from '../fund/fundArea'
import { chainOf, focusForTarget, parseFocus, serializeFocus, targetForFocus } from '../fund/focus'
import { ErrorState } from '../state'
import { formatOfficerDate } from '../../lib/officerFormat'
import {
  cardForPosition,
  recordHref,
  workbenchHref,
  recordQuery,
  subchainScope,
  type Catalogue,
  type RecordContext,
  type ViewScope,
} from './context'
import { useRead } from './useRead'
import ObjectContextNavigation from './ObjectContextNavigation'
import { objectContextNavigation, objectWorkContext, type FundRelationships } from './objectContext'
import OpenItems from './OpenItems'
import OpenItemsSummary from './OpenItemsSummary'
import FundClientFacts from './FundClientFacts'
import { overviewStructureFacts } from '../fund/overviewFacts'
import FundReporting, { ReportingDatesFact } from './FundReporting'
import { FundLatestActivity } from './FundHistory'
import type { ReportingSchedule } from './reportingTypes'
import '../fund/fundtabs.css'
import './navigation.css'
interface Contact {
  name: string | null
  email: string | null
  origin: 'fund' | 'fund_folder' | 'client' | 'unresolved' | 'legacy'
  warning: string | null
  requires_resolution: boolean
}
interface FundFacts {
  id: string
  name: string
  legal_form: string | null
  jurisdiction: string | null
  record_start_date: string | null
  reporting_dates?: string[]
}
function Overview({ scope }: { scope: ViewScope }) {
  const [params, setParams] = useSearchParams()
  const [retry, setRetry] = useState(0)
  const [summaryTarget, setSummaryTarget] = useState<HTMLDivElement | null>(null)
  const [reportingEditorOpen, setReportingEditorOpen] = useState(false)
  const [reportingRefresh, setReportingRefresh] = useState(0)
  const reporting = useRead<ReportingSchedule>(
    `/api/fund-reporting/funds/${scope.fund_id}`,
    reportingRefresh
  )
  const navigate = useNavigate()
  const read = useRead<RecordContext>(recordQuery(scope), retry)
  const fund = useRead<FundFacts>(`/api/funds/${scope.fund_id}`, retry)
  const contact = useRead<Contact>(
    `/api/intake/funds/${scope.fund_id}/contact${scope.client_id ? '?client_id=' + encodeURIComponent(scope.client_id) : ''}`,
    retry
  )
  const walk = useRead<{ nodes: WalkNode[] }>(`/api/funds/${scope.fund_id}/walk-status`, retry)
  const catalogue = useRead<Catalogue>('/api/navigation/catalogue', retry)
  const relationships = useRead<FundRelationships>(
    `/api/funds/${scope.fund_id}/masters?contract=relationships_v1`,
    retry
  )
  const value = read.value
  const table = value?.records.find((row) => row.fund_id === scope.fund_id)?.record
  const chain = useMemo(() => (table ? chainOf(table.segments) : []), [table])
  const structure = useMemo(() => (table ? overviewStructureFacts(table) : null), [table])
  const knownHead = table?.entities.find(
    (entity) => entity.is_fund_root && entity.state !== 'proposed'
  )?.id
  const rawFocus = params.get('focus')
  const focus = useMemo(() => {
    const parsed = parseFocus(rawFocus)
    const target = parsed ? targetForFocus(chain, parsed, knownHead) : null
    return target ? focusForTarget(chain, target, null, knownHead) : null
  }, [rawFocus, chain, knownHead])
  const target = focus ? targetForFocus(chain, focus, knownHead) : null
  const objectNavigation =
    value && target ? objectContextNavigation(value, target, scope, relationships.value) : null
  const open = (selected: DrawerTarget) => {
    const tradePosition =
      selected.type === 'trade'
        ? table?.trades.find((trade) => trade.id === selected.id)?.position_id
        : null
    const nextFocus = focusForTarget(chain, selected, tradePosition ?? null, knownHead)
    if (!nextFocus) return
    const next = new URLSearchParams(params)
    next.set('focus', serializeFocus(nextFocus))
    setParams(next)
  }
  const clear = () => {
    const next = new URLSearchParams(params)
    next.delete('focus')
    setParams(next)
  }
  const openRecord = (selected?: DrawerTarget) => {
    const position =
      selected?.type === 'position'
        ? selected.id
        : selected?.type === 'trade'
          ? table?.trades.find((trade) => trade.id === selected.id)?.position_id
          : null
    const card = position && value ? cardForPosition(value.work, position) : null
    navigate(recordHref(card ? subchainScope(card, scope) : scope))
  }
  const matrix = (value?.records.find((row) => row.fund_id === scope.fund_id)?.matrix ??
    []) as unknown as MatrixRow[]
  return (
    <main className="cockpit fa-page">
      <div className="fa" data-testid="context-fund-overview">
        <header className="fa-head context-fund-header">
          <nav className="nav-context-links">
            <Link to="/v2/funds">‹ Funds</Link>
            {value?.client ? (
              <Link to={recordHref({ kind: 'client', client_id: value.client.id })}>
                {value.client.display_name ?? 'Client'}
              </Link>
            ) : null}
          </nav>
          <div className="context-fund-heading">
            {catalogue.value ? (
              <h1 className="fa-name">
                <FundSelector
                  funds={catalogue.value.funds.map((item) => ({
                    id: item.id,
                    name: item.name,
                    open: item.open_subchain_count,
                    sub: `${item.queues.book.card_count} Book · ${item.queues.evidence.card_count} Evidence · ${item.queues.chase.card_count} Chase`,
                  }))}
                  value={scope.fund_id ?? null}
                  onChoose={(id) => {
                    rememberFundChoice(id)
                    navigate(id ? `/v2/funds/${id}` : '/v2/funds')
                  }}
                  noun="open subchains"
                />
              </h1>
            ) : (
              <h1>{table?.fund_name ?? fund.value?.name ?? 'Fund'}</h1>
            )}
            <div className="context-fund-summary" ref={setSummaryTarget} />
          </div>
          <div className="fa-tabs">
            <span className="fa-tab on" aria-current="page">
              Overview
            </span>
            <Link className="fa-tab" to={recordHref(scope)}>
              Record
            </Link>
            <div className="context-fund-work-links">
              <OpenItemsSummary
                work={value?.work ?? null}
                selected={null}
                onSelect={(queue) => navigate(workbenchHref(scope, queue))}
                links={{
                  book: workbenchHref(scope, 'book'),
                  evidence: workbenchHref(scope, 'evidence'),
                  chase: workbenchHref(scope, 'chase'),
                }}
              />
            </div>
          </div>
        </header>
        {read.loading ? <p role="status">Loading fund record…</p> : null}
        {read.error ? (
          <ErrorState
            what="fund record"
            error={read.error}
            onRetry={() => setRetry((n) => n + 1)}
          />
        ) : null}
        {value && table ? (
          <>
            {value.client_match_is_provisional ? (
              <p className="nav-muted">Client context is a provisional folder match.</p>
            ) : null}
            <FundOverviewTab
              contextual
              chainAttention={
                walk.error
                  ? 'Look-through decisions unavailable'
                  : walk.loading
                    ? 'Loading look-through decisions…'
                    : walk.value?.nodes.some((node) => node.state === 'open')
                      ? 'Look-through review needed'
                      : null
              }
              summaryTarget={summaryTarget}
              fundId={scope.fund_id ?? undefined}
              table={table}
              matrix={matrix}
              walk={walk.value?.nodes ?? []}
              focus={focus}
              onOpen={open}
              onClearFocus={clear}
              onJump={openRecord}
            />
            {walk.error ? (
              <ErrorState
                what="look-through decisions"
                error={walk.error}
                onRetry={() => setRetry((n) => n + 1)}
                compact
              />
            ) : null}
            <FundReporting
              collapsible
              fundId={scope.fund_id!}
              scope={scope}
              work={value.work}
              schedule={reporting}
              refresh={reportingRefresh}
              onRefresh={() => setReportingRefresh((n) => n + 1)}
              editorOpen={reportingEditorOpen}
              onEdit={() => setReportingEditorOpen(true)}
              onCloseEditor={() => setReportingEditorOpen(false)}
            />
            <OpenItems work={value.work} scope={scope} showSummary={false} collapsible />
            <FundLatestActivity scope={scope} refresh={retry} collapsible />
            <FundSection
              title="Fund facts"
              testId="fund-facts-section"
              attention={
                fund.error || contact.error || catalogue.error
                  ? 'Some fund facts are unavailable'
                  : fund.loading || contact.loading || catalogue.loading
                    ? 'Loading fund facts…'
                    : contact.value?.requires_resolution
                      ? 'Fund contact needs review'
                      : (contact.value?.warning ?? null)
              }
            >
              <dl className="nav-facts-grid">
                <div data-testid="fund-fact-asset-classes">
                  <dt>Asset classes</dt>
                  <dd>{structure?.classes.join(' · ') || 'Not recorded'}</dd>
                </div>
                <div data-testid="fund-fact-structure">
                  <dt>Structure</dt>
                  <dd>
                    {structure ? (
                      <>
                        {structure.levels} {structure.levels === 1 ? 'level' : 'levels'} below the
                        fund
                        {' · '}
                        {structure.targets} {structure.targets === 1 ? 'target' : 'targets'}
                      </>
                    ) : (
                      'Not recorded'
                    )}
                  </dd>
                </div>
                <div>
                  <dt>Legal form / domicile</dt>
                  <dd>
                    {[fund.value?.legal_form, fund.value?.jurisdiction]
                      .filter(Boolean)
                      .join(' · ') || 'Not recorded'}
                  </dd>
                </div>
                <div>
                  <dt>Position currencies</dt>
                  <dd>
                    {[
                      ...new Set(
                        table.positions.map((position) => position.units_currency).filter(Boolean)
                      ),
                    ].join(' · ') || 'Not recorded'}
                  </dd>
                </div>
                <div>
                  <dt>Record start</dt>
                  <dd>
                    {fund.value?.record_start_date
                      ? formatOfficerDate(fund.value.record_start_date)
                      : 'Not recorded'}
                  </dd>
                </div>
                <div>
                  <dt>Reporting dates</dt>
                  <dd>
                    <ReportingDatesFact
                      read={reporting}
                      onEdit={() => setReportingEditorOpen(true)}
                    />
                  </dd>
                </div>
                <div>
                  <dt>Fund contact</dt>
                  <dd>
                    {contact.loading
                      ? 'Loading…'
                      : contact.error
                        ? 'Contact unavailable'
                        : (contact.value?.email ?? 'Recipient not set')}
                    {contact.value?.name ? <small>{contact.value.name}</small> : null}
                    {contact.value && ['client', 'fund_folder'].includes(contact.value.origin) ? (
                      <small>
                        {contact.value.origin === 'client'
                          ? 'Client contact fallback'
                          : 'Fund-folder contact fallback'}
                      </small>
                    ) : null}
                    {contact.value?.warning ? <small>{contact.value.warning}</small> : null}
                  </dd>
                </div>
                <FundClientFacts
                  fundId={scope.fund_id!}
                  hints={
                    catalogue.value
                      ? (catalogue.value.funds.find((item) => item.id === scope.fund_id)
                          ?.client_hints ?? [])
                      : null
                  }
                  selectedClient={value.client}
                  selectedClientId={scope.client_id}
                  loading={catalogue.loading}
                  error={catalogue.error}
                  refresh={retry}
                  onRefresh={() => setRetry((n) => n + 1)}
                />
              </dl>
            </FundSection>
            {fund.error ? (
              <ErrorState
                what="fund facts"
                error={fund.error}
                onRetry={() => setRetry((n) => n + 1)}
                compact
              />
            ) : null}
            {contact.error ? (
              <ErrorState
                what="fund contact"
                error={contact.error}
                onRetry={() => setRetry((n) => n + 1)}
                compact
              />
            ) : null}
            {target ? (
              <ObjectDrawer
                key={target.id}
                fundId={scope.fund_id!}
                target={target}
                addMovementHref={
                  target.type === 'position' || target.type === 'instrument'
                    ? manualMovementHref(scope.fund_id!, { kind: target.type, id: target.id })
                    : undefined
                }
                onClose={clear}
                onShowInRecord={
                  objectNavigation?.choices.length ? undefined : () => openRecord(target)
                }
                workContext={
                  objectNavigation ? objectWorkContext(value, scope, objectNavigation) : undefined
                }
                contextNavigation={
                  objectNavigation ? (
                    <ObjectContextNavigation
                      navigation={objectNavigation}
                      loading={relationships.loading}
                      unavailable={!!relationships.error}
                      partial={relationships.value?.scope_incomplete}
                      onRetry={() => setRetry((n) => n + 1)}
                    />
                  ) : null
                }
              />
            ) : null}
          </>
        ) : null}
      </div>
    </main>
  )
}
export default function ContextFundOverview() {
  const { id } = useParams<{ id: string }>()
  const [params] = useSearchParams()
  if (!id) return <main className="detail">Fund context unavailable.</main>
  const scope: ViewScope = { kind: 'fund', fund_id: id, client_id: params.get('client') }
  if (params.get('tab') === 'record')
    return (
      <Navigate
        to={recordHref(scope, { asOf: params.get('asof'), focus: params.get('focus') })}
        replace
      />
    )
  return <Overview key={id + ':' + (scope.client_id ?? '')} scope={scope} />
}
